import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Bot, ChevronDown, ChevronRight, Plus, Save } from "lucide-react";
import { PageHeader } from "../../components/layout/PageHeader";
import {
  createAiAgent,
  getAiAgent,
  listAiAgents,
  updateAiAgent,
  type AiAgentDetail,
  type AiAgentListItem,
  type AiAgentPayload,
} from "../../api/aiAgentAdminApi";
import { useLookup } from "../../flowbuilder/lookups";

type LoadState =
  | { status: "loading" }
  | { status: "ready"; items: AiAgentListItem[] }
  | { status: "error"; message: string };

/**
 * Admin-only editor for `dbo.aiServerAgent` rows — the voicebot agents
 * used by the new Mod_VB_* flow modules. Lists every agent on the left,
 * shows an inline edit form on the right.
 *
 * The "Voice preset" field binds to the new `voicePresetKey` column.
 * The legacy `voiceName` / `voiceId` columns are deliberately moved
 * into an "Advanced (legacy)" foldout with a banner explaining that the
 * preset is the new way; the runtime still falls back to voiceName when
 * voicePresetKey is NULL.
 */
export function AiAgentsAdminPage() {
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  const reload = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const resp = await listAiAgents();
      setState({ status: "ready", items: resp.items ?? [] });
    } catch (e) {
      setState({
        status: "error",
        message: e instanceof Error ? e.message : String(e),
      });
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Auto-select the first row so the editor is never empty when there
  // is at least one agent to show.
  useEffect(() => {
    if (state.status !== "ready" || creating || selectedId != null) return;
    const first = state.items[0];
    if (first) setSelectedId(first.aiServerAgentId);
  }, [state, creating, selectedId]);

  const startNew = () => {
    setSelectedId(null);
    setCreating(true);
  };

  return (
    <>
      <PageHeader
        title="Voicebot agents"
        actions={
          <button
            type="button"
            className="btn btn-primary"
            onClick={startNew}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <Plus size={16} />
            New agent
          </button>
        }
      />
      <div className="container" style={{ padding: "0 0 32px" }}>
        {state.status === "loading" && <div className="tile">Loading…</div>}

        {state.status === "error" && (
          <div className="tile" style={{ borderColor: "var(--portal-danger)" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <AlertTriangle size={18} />
              <span>Could not load agents: {state.message}</span>
            </div>
          </div>
        )}

        {state.status === "ready" && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(260px, 320px) 1fr",
              gap: 16,
              alignItems: "start",
            }}
          >
            <AgentList
              items={state.items}
              selectedId={selectedId}
              onSelect={(id) => {
                setCreating(false);
                setSelectedId(id);
              }}
            />

            <div className="tile" style={{ padding: 0 }}>
              {creating ? (
                <AgentForm
                  key="new"
                  mode="create"
                  onSaved={async (newId) => {
                    await reload();
                    setCreating(false);
                    setSelectedId(newId);
                  }}
                  onCancel={() => setCreating(false)}
                />
              ) : selectedId != null ? (
                <AgentForm
                  key={selectedId}
                  mode="edit"
                  aiServerAgentId={selectedId}
                  onSaved={async () => {
                    await reload();
                  }}
                />
              ) : (
                <div style={{ padding: 24, color: "var(--portal-muted)" }}>
                  Select an agent on the left, or use "New agent" to create one.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function AgentList({
  items,
  selectedId,
  onSelect,
}: {
  items: AiAgentListItem[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}) {
  if (items.length === 0) {
    return (
      <div className="tile">
        <div style={{ color: "var(--portal-muted)" }}>No agents yet.</div>
      </div>
    );
  }
  return (
    <div className="tile" style={{ padding: 0 }}>
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {items.map((a) => {
          const active = a.aiServerAgentId === selectedId;
          return (
            <li key={a.aiServerAgentId}>
              <button
                type="button"
                onClick={() => onSelect(a.aiServerAgentId)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  padding: "10px 14px",
                  background: active ? "var(--portal-primary-soft, #eef2ff)" : "transparent",
                  border: "none",
                  borderBottom: "1px solid var(--portal-border, #e5e7eb)",
                  cursor: "pointer",
                  textAlign: "left",
                  color: "inherit",
                  font: "inherit",
                }}
              >
                <Bot
                  size={16}
                  style={{
                    color: active ? "var(--portal-primary)" : "var(--portal-muted)",
                    flexShrink: 0,
                  }}
                />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {a.name || "(unnamed)"}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--portal-muted)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={
                      a.voicePresetKey
                        ? `Preset: ${a.voicePresetKey}`
                        : a.voiceName
                        ? `Legacy voice: ${a.voiceName}`
                        : "No voice set"
                    }
                  >
                    {a.voicePresetKey
                      ? `Preset: ${a.voicePresetKey}`
                      : a.voiceName
                      ? `Legacy: ${a.voiceName}`
                      : "—"}
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const EMPTY_DETAIL: AiAgentDetail = {
  aiServerAgentId: 0,
  name: "",
  description: "",
  instructions: "",
  welcomeMsg: "",
  voicePresetKey: null,
  voiceName: null,
  ttsModel: "v2",
  allowAgentConnect: false,
  allowHuntgroupConnect: false,
  allowProfileFetch: false,
};

interface AgentFormProps {
  mode: "create" | "edit";
  aiServerAgentId?: number;
  onSaved: (id: number) => void | Promise<void>;
  onCancel?: () => void;
}

function AgentForm({ mode, aiServerAgentId, onSaved, onCancel }: AgentFormProps) {
  const [detail, setDetail] = useState<AiAgentDetail | null>(
    mode === "create" ? { ...EMPTY_DETAIL } : null,
  );
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [legacyOpen, setLegacyOpen] = useState(false);

  useEffect(() => {
    if (mode === "create") return;
    if (aiServerAgentId == null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAiAgent(aiServerAgentId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, aiServerAgentId]);

  const patch = (p: Partial<AiAgentDetail>) => {
    setDetail((d) => (d ? { ...d, ...p } : d));
    setSuccess(false);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!detail) return;
    setError(null);
    setSuccess(false);
    setSaving(true);
    try {
      const payload: AiAgentPayload = {
        name: detail.name.trim(),
        description: detail.description ?? null,
        instructions: detail.instructions ?? "",
        welcomeMsg: detail.welcomeMsg ?? null,
        voicePresetKey: detail.voicePresetKey ?? null,
        voiceName: detail.voiceName ?? null,
        ttsModel: detail.ttsModel || "v2",
        allowAgentConnect: detail.allowAgentConnect,
        allowHuntgroupConnect: detail.allowHuntgroupConnect,
        allowProfileFetch: detail.allowProfileFetch,
      };
      if (mode === "create") {
        const res = await createAiAgent(payload);
        await onSaved(res.id);
      } else if (aiServerAgentId != null) {
        await updateAiAgent(aiServerAgentId, payload);
        setSuccess(true);
        await onSaved(aiServerAgentId);
      }
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;
  if (!detail) return <div style={{ padding: 24, color: "var(--portal-muted)" }}>—</div>;

  return (
    <form onSubmit={submit} style={{ padding: 20 }}>
      <h3 style={{ margin: "0 0 16px", fontSize: 16 }}>
        {mode === "create" ? "New agent" : `Agent #${detail.aiServerAgentId}`}
      </h3>

      <Field label="Name" required>
        <input
          type="text"
          className="form-input"
          value={detail.name}
          onChange={(e) => patch({ name: e.target.value })}
          maxLength={150}
          required
          style={inputStyle}
        />
      </Field>

      <Field label="Description">
        <input
          type="text"
          value={detail.description ?? ""}
          onChange={(e) => patch({ description: e.target.value })}
          maxLength={1000}
          style={inputStyle}
        />
      </Field>

      <Field label="Instructions" required>
        <textarea
          value={detail.instructions ?? ""}
          onChange={(e) => patch({ instructions: e.target.value })}
          required
          rows={8}
          style={{ ...inputStyle, fontFamily: "var(--portal-mono, monospace)", resize: "vertical" }}
        />
      </Field>

      <Field label="Welcome message">
        <textarea
          value={detail.welcomeMsg ?? ""}
          onChange={(e) => patch({ welcomeMsg: e.target.value })}
          rows={3}
          maxLength={4000}
          style={{ ...inputStyle, resize: "vertical" }}
        />
      </Field>

      <Field
        label="Voice preset"
        hint="The new voicebot voice catalog reference. Stored as Voices.PresetKey."
      >
        <VoicePresetSelect
          value={detail.voicePresetKey ?? ""}
          onChange={(v) => patch({ voicePresetKey: v || null })}
        />
      </Field>

      <Field label="TTS model">
        <input
          type="text"
          value={detail.ttsModel ?? "v2"}
          onChange={(e) => patch({ ttsModel: e.target.value })}
          maxLength={50}
          style={inputStyle}
        />
      </Field>

      <Field label="Capabilities">
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <CheckboxRow
            label="Allow agent connect"
            value={detail.allowAgentConnect ?? false}
            onChange={(v) => patch({ allowAgentConnect: v })}
          />
          <CheckboxRow
            label="Allow huntgroup connect"
            value={detail.allowHuntgroupConnect}
            onChange={(v) => patch({ allowHuntgroupConnect: v })}
          />
          <CheckboxRow
            label="Allow profile fetch"
            value={detail.allowProfileFetch}
            onChange={(v) => patch({ allowProfileFetch: v })}
          />
        </div>
      </Field>

      {/* Legacy foldout — kept visible so existing agents are never silently broken. */}
      <div
        style={{
          marginTop: 16,
          border: "1px solid var(--portal-border, #e5e7eb)",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        <button
          type="button"
          onClick={() => setLegacyOpen((v) => !v)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            width: "100%",
            padding: "10px 12px",
            background: "var(--portal-warn-soft, #fff7ed)",
            border: "none",
            color: "inherit",
            font: "inherit",
            cursor: "pointer",
            textAlign: "left",
          }}
        >
          {legacyOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <strong>Advanced (legacy)</strong>
          <span style={{ color: "var(--portal-muted)", fontSize: 12 }}>
            voiceName / voiceId — deprecated, runtime fallback only
          </span>
        </button>
        {legacyOpen && (
          <div style={{ padding: 12 }}>
            <div
              style={{
                padding: "8px 10px",
                background: "var(--portal-warn-soft, #fff7ed)",
                border: "1px solid var(--portal-warn, #f59e0b)",
                borderRadius: 6,
                color: "var(--portal-warn-fg, #92400e)",
                fontSize: 13,
                marginBottom: 12,
              }}
            >
              Voice preset is the new way to assign a voice. The legacy
              <code> voiceName</code> / <code>voiceId</code> fields are still
              consulted by the runtime as a fallback when
              <code> voicePresetKey</code> is empty.
            </div>
            <Field label="Voice name (legacy)">
              <input
                type="text"
                value={detail.voiceName ?? ""}
                onChange={(e) => patch({ voiceName: e.target.value })}
                maxLength={50}
                style={inputStyle}
              />
            </Field>
          </div>
        )}
      </div>

      {error && (
        <div
          style={{
            marginTop: 12,
            padding: "8px 10px",
            color: "var(--portal-danger-fg, #991b1b)",
            background: "var(--portal-danger-soft, #fef2f2)",
            border: "1px solid var(--portal-danger, #ef4444)",
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}
      {success && !error && (
        <div
          style={{
            marginTop: 12,
            padding: "8px 10px",
            color: "var(--portal-success-fg, #065f46)",
            background: "var(--portal-success-soft, #ecfdf5)",
            border: "1px solid var(--portal-success, #10b981)",
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          Saved.
        </div>
      )}

      <div style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "flex-end" }}>
        {onCancel && (
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="btn btn-primary"
          disabled={saving}
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <Save size={14} />
          {saving ? "Saving…" : mode === "create" ? "Create" : "Save"}
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
        {label}
        {required && <span style={{ color: "var(--portal-danger, #dc2626)" }}> *</span>}
      </label>
      {children}
      {hint && (
        <div style={{ fontSize: 12, color: "var(--portal-muted)", marginTop: 4 }}>{hint}</div>
      )}
    </div>
  );
}

function CheckboxRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label style={{ display: "inline-flex", gap: 8, alignItems: "center", cursor: "pointer" }}>
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "6px 10px",
  border: "1px solid var(--portal-border, #d1d5db)",
  borderRadius: 6,
  fontSize: 14,
  background: "var(--portal-input-bg, #fff)",
  color: "inherit",
};

/**
 * Standalone VoicePreset picker for the admin form. We can't reuse the
 * Flow Builder's `LookupEditor` here because that component lives
 * inside the flowbuilder folder and depends on its surrounding
 * `LookupProvider`. The data layer (`useLookup`) is shared, so this
 * thin wrapper renders a grouped <select> with the same source data.
 */
function VoicePresetSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  // The voicepreset fetcher ignores applicationId; we pass 0 so the
  // cache key is stable across admin sessions.
  const { items, loading, error } = useLookup("voicepreset", 0);

  const groups = useMemo(() => {
    const map = new Map<string, typeof items>();
    for (const it of items) {
      const cat = it.primaryLanguage ?? "—";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(it);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [items]);

  if (error) {
    return (
      <div style={{ color: "var(--portal-danger)", fontSize: 13 }}>
        Failed to load voice presets: {error}
      </div>
    );
  }

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={loading}
      style={inputStyle}
    >
      <option value="">{loading ? "Loading…" : "— None (use legacy fallback) —"}</option>
      {groups.map(([lang, list]) => (
        <optgroup key={lang} label={lang}>
          {list.map((p) => (
            <option key={String(p.id)} value={String(p.id)}>
              {p.name}
              {p.providerVoiceId ? ` · ${p.providerVoiceId}` : ""}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}
